import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { formatDate, handleErrors } from '../../utils/common';

const Seo = () => {
    const Swal = window.Swal;

    const limit = 5;
    const [seoEntries, setSeoEntries] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSeoEntries = async (page = 1) => {
            try {
                const offset = (page - 1) * limit;
                const res = await api.get(`/seo/list?limit=${limit}&offset=${offset}`);
                if (res.status === 200) {
                    setSeoEntries(res.data.seo);
                    const total = res.data.total || 0;
                    setTotalPages(Math.ceil(total / limit));
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSeoEntries(currentPage);
    }, [currentPage]);

    const handleDelete = (seoId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This will delete the SEO entry.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.delete(`/seo/${seoId}`);
                    if (res.status === 200) {
                        showToast('success', res.data.message, 'Success');
                        setSeoEntries((prev) => prev.filter((entry) => entry._id !== seoId));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    const generatePagination = () => {
        const pages = [];
        const maxVisible = 5;
        const sidePages = 2;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > sidePages + 2) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - sidePages);
            const end = Math.min(totalPages - 1, currentPage + sidePages);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - sidePages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.SEO}>SEO</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">SEO</h4>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Page Code</th>
                                                <th>Page Title</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seoEntries && seoEntries.map((entry, index) => (
                                                <tr key={entry._id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{entry.page_code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                                                    <td>{entry.page_title}</td>
                                                    <td>{formatDate(entry.t)}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <Link to={`/seo/${entry._id}/edit`} className="btn btn-primary shadow btn-xs sharp me-1">
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Link>
                                                            <a
                                                                href="#/"
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                onClick={() => handleDelete(entry._id)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>

                                {generatePagination().map((page, index) =>
                                    page === '...' ? (
                                        <li className="page-item disabled" key={index}>
                                            <span className="page-link">...</span>
                                        </li>
                                    ) : (
                                        <li className={`page-item ${currentPage === page ? 'active' : ''}`} key={index}>
                                            <a className="page-link" href="#/" onClick={() => setCurrentPage(page)}>{page}</a>
                                        </li>
                                    )
                                )}

                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Seo;
